import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Share2,
  Copy,
  Users,
  DollarSign,
  Tag,
  Loader2,
  TrendingUp,
  Gift
} from 'lucide-react';
import { toast } from 'sonner';
import { useReferralStats } from '@/hooks';
import { useAffiliatePayment } from '@/hooks/useAffiliatePayment';
import { JoinAffiliateProgramForm } from '@/components/affiliate/JoinAffiliateProgramForm';

export function ReferralTab() {
  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  
  const { data: stats, isLoading, error } = useReferralStats();
  const affiliatePayment = useAffiliatePayment();

  // Check user state for referral program
  const isAffiliate = stats?.isAffiliate === true;
  const hasPaidAccess = stats?.hasPaidAffiliateAccess === true;
  
  // Three states: not affiliate -> join form, affiliate but not paid -> paywall, paid -> full access
  const showJoinForm = !isAffiliate;
  const showPaywall = isAffiliate && !hasPaidAccess;

  const copyCodeToClipboard = async () => {
    if (!stats?.promoCode) return;
    try {
      await navigator.clipboard.writeText(stats.promoCode);
      setCopiedCode(true);
      toast.success('Promo code copied to clipboard!');
      setTimeout(() => setCopiedCode(false), 2000);
    } catch (err) {
      toast.error('Failed to copy promo code');
    }
  };

  const copyReferralLinkToClipboard = async () => {
    if (!stats?.rewardfulToken) return;
    const referralLink = `${window.location.origin}/affiliate/signup?via=${stats.rewardfulToken}`;
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopiedLink(true);
      toast.success('Referral link copied to clipboard!');
      setTimeout(() => setCopiedLink(false), 2000);
    } catch (err) {
      toast.error('Failed to copy referral link');
    }
  };

  const handleAffiliatePayment = async () => {
    try {
      await affiliatePayment.mutateAsync();
    } catch (error) {
      console.error('Payment error:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2">Loading referral data...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <p className="text-destructive mb-2">Failed to load referral data</p>
          <p className="text-sm text-muted-foreground">{error.message}</p>
        </div>
      </div>
    );
  }

  // Show join form as a WALL - force users to become affiliates to access referral features
  if (showJoinForm) {
    return (
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader className="text-center">
            <div className="rounded-full bg-primary/10 w-12 h-12 flex items-center justify-center mx-auto mb-3">
              <TrendingUp className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-xl">
              Join Our Affiliate Program
            </CardTitle>
            <CardDescription>
              {stats?.commission?.type === 'percent' 
                ? `Earn ${stats.commission.value}% commission on referrals` 
                : stats?.commission?.type === 'amount'
                ? `Earn $${(stats.commission.cents! / 100).toFixed(2)} ${stats.commission.currency?.toUpperCase()} per referral`
                : 'Earn commission on referrals'} and get your personalized promo code
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Benefits */}
            <div className="grid grid-cols-3 gap-4 mb-6 p-4 bg-muted/50 rounded-lg">
              <div className="text-center">
                <DollarSign className="h-5 w-5 text-muted-foreground mx-auto mb-1" />
                <p className="text-xs font-medium">
                  {stats?.commission?.type === 'percent' 
                    ? `${stats.commission.value}% Commission` 
                    : stats?.commission?.type === 'amount'
                    ? `$${(stats.commission.cents! / 100).toFixed(2)} ${stats.commission.currency?.toUpperCase()}`
                    : 'Commission'}
                </p>
              </div>
              <div className="text-center">
                <Tag className="h-5 w-5 text-muted-foreground mx-auto mb-1" />
                <p className="text-xs font-medium">Custom Code</p>
              </div>
              <div className="text-center">
                <Gift className="h-5 w-5 text-muted-foreground mx-auto mb-1" />
                <p className="text-xs font-medium">Monthly Payouts</p>
              </div>
            </div>

            {/* Embedded Form */}
            <JoinAffiliateProgramForm />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show paywall for affiliates who haven't paid $49
  if (showPaywall) {
    return (
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader className="text-center">
            <div className="rounded-full bg-primary/10 w-12 h-12 flex items-center justify-center mx-auto mb-3">
              <DollarSign className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-xl">
              Unlock Referral Program Access
            </CardTitle>
            <CardDescription>
              Pay $49 one-time to access all referral tools and start earning commissions
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 mb-6 p-4 bg-muted/50 rounded-lg">
              <div className="text-center">
                <Share2 className="h-5 w-5 text-muted-foreground mx-auto mb-1" />
                <p className="text-xs font-medium">Referral Links</p>
              </div>
              <div className="text-center">
                <Tag className="h-5 w-5 text-muted-foreground mx-auto mb-1" />
                <p className="text-xs font-medium">Custom Codes</p>
              </div>
              <div className="text-center">
                <DollarSign className="h-5 w-5 text-muted-foreground mx-auto mb-1" />
                <p className="text-xs font-medium">Earn Commissions</p>
              </div>
            </div>

            <div className="text-center mb-6">
              <div className="text-3xl font-bold text-primary mb-2">$49</div>
              <p className="text-sm text-muted-foreground">One-time payment • Lifetime access</p>
            </div>

            <Button
              className="w-full"
              size="lg"
              onClick={handleAffiliatePayment}
              disabled={affiliatePayment.isPending}
            >
              {affiliatePayment.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <DollarSign className="w-4 h-4 mr-2" />
                  Pay $49 & Unlock Access
                </>
              )}
            </Button>
            
            <p className="text-xs text-center text-muted-foreground mt-4">
              Secure payment via Stripe • Cancel anytime
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="flex items-center justify-center p-8">
        <p className="text-muted-foreground">No referral data available</p>
      </div>
    );
  }

  // Show full referral tools for paid affiliates
  return (
    <div className="space-y-6">
      {/* Main Referral Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5" />
            Invite Friends & Earn Rewards
          </CardTitle>
          <CardDescription>
            Share ScheduleHero AI with friends and {stats?.commission?.type === 'percent' 
              ? `earn ${stats.commission.value}% commission on their purchases`
              : stats?.commission?.type === 'amount'
              ? `earn $${(stats.commission.cents! / 100).toFixed(2)} ${stats.commission.currency?.toUpperCase()} per purchase`
              : 'earn commission on their purchases'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Referral Link Section */}
          <div className="space-y-3">
            <label className="text-sm font-medium">Your Referral Link</label>
            <div className="flex gap-2">
              <Input
                value={`${window.location.origin}/affiliate/signup?via=${stats.rewardfulToken || ''}`}
                readOnly
                className="flex-1"
              />
              <Button
                onClick={copyReferralLinkToClipboard}
                variant={copiedLink ? "default" : "outline"}
                className="min-w-[100px]"
              >
                {copiedLink ? (
                  <>
                    <Copy className="h-4 w-4 mr-2" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Share2 className="h-4 w-4 mr-2" />
                    Copy
                  </>
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Share this link so friends can sign up with your referral tracking automatically applied
            </p>
          </div>

          {/* Promo Code Section */}
          <div className="space-y-3">
            <label className="text-sm font-medium">Your Promo Code</label>
            <div className="flex gap-2">
              <Input
                value={stats.promoCode || ''}
                readOnly
                className="flex-1"
              />
              <Button
                onClick={copyCodeToClipboard}
                variant={copiedCode ? "default" : "outline"}
                className="min-w-[100px]"
              >
                {copiedCode ? (
                  <>
                    <Copy className="h-4 w-4 mr-2" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Tag className="h-4 w-4 mr-2" />
                    Copy
                  </>
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Friends can enter this code at checkout instead of using the link
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Referred Users</p>
                <p className="text-2xl font-bold">{stats.referredUsers}</p>
              </div>
              <Users className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Earned</p>
                <p className="text-2xl font-bold">${stats.totalEarned.toFixed(2)}</p>
              </div>
              <DollarSign className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Status</p>
                <Badge variant={stats.referrals.length > 0 ? "default" : "secondary"}>
                  {stats.referrals.length > 0 ? 'Active' : 'No Referrals Yet'}
                </Badge>
              </div>
              <Share2 className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* How it Works */}
      <Card>
        <CardHeader>
          <CardTitle>How It Works</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="rounded-full bg-primary/10 w-12 h-12 flex items-center justify-center mx-auto mb-3">
                <Share2 className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-medium mb-2">1. Share Your Code</h3>
              <p className="text-sm text-muted-foreground">
                Share your promo code with friends, colleagues, or on social media
              </p>
            </div>
            
            <div className="text-center">
              <div className="rounded-full bg-primary/10 w-12 h-12 flex items-center justify-center mx-auto mb-3">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-medium mb-2">2. They Sign Up</h3>
              <p className="text-sm text-muted-foreground">
                When someone uses your code at checkout, they get {stats?.discountPercent ? `${stats.discountPercent}%` : 'a'} discount
              </p>
            </div>
            
            <div className="text-center">
              <div className="rounded-full bg-primary/10 w-12 h-12 flex items-center justify-center mx-auto mb-3">
                <DollarSign className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-medium mb-2">3. You Earn Rewards</h3>
              <p className="text-sm text-muted-foreground">
                Receive {stats?.commission?.type === 'percent' 
                  ? `${stats.commission.value}% commission` 
                  : stats?.commission?.type === 'amount'
                  ? `$${(stats.commission.cents! / 100).toFixed(2)} ${stats.commission.currency?.toUpperCase()}`
                  : 'commission'} on their purchase, paid out monthly
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}